import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";

/**
 * Custom Hook to handle the state and logic of preparation (picking) of products
 *
 * @param {object} order - The object of the current order containing the products
 * @param {function} onSuccess - Callback that runs when a product is successfully prepared (e.g. refetchOrder)
 * @param {function(string): void} onError - Callback that runs when an error occurs, receives the error message
 * * @returns {{
 * pickingQuantities: object,
 * submittingProductId: number | null,
 * handleIncrementQuantity: (productId: number, maxQuantity: number) => void,
 * handleDecrementQuantity: (productId: number) => void,
 * handlePrepareProductIndividual: (orderProductId: number) => Promise<void>
 * }}
 */
export function useOrderPicking(order, onSuccess, onError) {
  // Data status and IU
  const [pickingQuantities, setPickingQuantities] = useState({});
  const [submittingProductId, setSubmittingProductId] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleIncrementQuantity = useCallback((productId, maxQuantity) => {
    setPickingQuantities((prev) => {
      const current = prev[productId] || 0;
      if (current >= maxQuantity) return prev;
      return { ...prev, [productId]: current + 1 };
    });
  }, []);

  const handleDecrementQuantity = useCallback((productId) => {
    setPickingQuantities((prev) => {
      const current = prev[productId] || 0;
      if (current <= 0) return prev;
      return { ...prev, [productId]: current - 1 };
    });
  }, []);

  const handlePrepareProductIndividual = useCallback(
    async (orderProductId) => {
      const quantityToPrepare = pickingQuantities[orderProductId] || 0;
      if (quantityToPrepare === 0) return;

      setSubmittingProductId(orderProductId);
      try {
        await axios.patch(`order_line_items/${orderProductId}/prepare/`, {
          prepared_quantity: quantityToPrepare,
        });
        if (onSuccess) {
          await onSuccess();
        }
      } catch (error) {
        console.error("Error al preparar producto:", error);
        if (onError) {
          onError("Ocurrió un error al registrar la preparación del producto");
        }
      } finally {
        setSubmittingProductId(null);
      }
    },
    [pickingQuantities, onSuccess, onError],
  );

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    if (order && order.products) {
      const initialQuantities = {};
      order.products.forEach((item) => {
        initialQuantities[item.id] = item.prepared_quantity || 0;
      });
      setPickingQuantities(initialQuantities);
    }
  }, [order]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    pickingQuantities,
    submittingProductId,

    // MANNERS (Setters y Actions)
    handleIncrementQuantity,
    handleDecrementQuantity,
    handlePrepareProductIndividual,
  };
}
